import React, { useRef, useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, Pressable, Platform, Animated, Easing, TextInput, Image, ScrollView, Dimensions, useWindowDimensions } from 'react-native';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient, GradientOverlay } from './Gradients';
import GradientButton from './GradientButton';
import GradientText from './GradientText';
import { getSportsbookLogo, getSportsbookUrl, getPlayerInitials, isPlayerProp, getTeamLogo } from '../data/logos';
import { styles } from './legBetCardStyles';
import LegBetCardView from './LegBetCardView';
// v1.18 P1 — canonical EV/devig math shared with UnifiedBetCard, BetCardV2 and
// the results sort. LegBetCard previously reimplemented devigging inline (with
// a broken "additive" formula and a heuristic power solver); all math now
// flows through utils/canonicalEv so every card surface agrees.
import { computeDevigMethodsSnapshot, cleanPerBookEv } from '../utils/canonicalEv';
import { useConsensusOptions } from '../utils/useConsensusOptions';
import * as betMath from '../utils/betMath';

// ⭐ Helper to detect sport type for proper photo display
// Returns: { isSinglePlayer: bool, isCombatSport: bool }
// - Single player (tennis, golf): Show ONE headshot in header, hide team logos
// - Combat sport (MMA, boxing): Show BOTH fighters in team logos area, no headshot
// - Team sport: Show team logos normally
const detectSportType = (sport, league, title = '', description = '') => {
    const s = (sport || '').toLowerCase();
    const l = (league || '').toLowerCase();
    const t = (title || '').toLowerCase();
    const d = (description || '').toLowerCase();
    
    // Single-player sports (tennis, golf) - show ONE headshot
    const isSinglePlayer = s.includes('tennis') || l.includes('atp') || l.includes('wta') || l.includes('itf') ||
                          s.includes('golf') || l.includes('pga') || l.includes('lpga');
    
    // Combat sports (MMA, boxing) - show BOTH fighters in team logos area
    let isCombatSport = s.includes('boxing') || s.includes('mma') || s.includes('combat') || s.includes('fighting') ||
                        l.includes('ufc') || l.includes('bellator') || l.includes('pfl') || l.includes('boxing') ||
                        l.includes('one championship') || l.includes('one_championship');
    
    // ⭐ FALLBACK: Check title/description for combat sport indicators
    if (!isCombatSport && !isSinglePlayer) {
        const combatMarkets = ['moneyline', 'fight result', 'method of victory', 'round betting', 
                              'total rounds', 'fight to go the distance', 'ko/tko', 'submission',
                              'decision', 'points'];
        const hasCombatMarket = combatMarkets.some(m => t.includes(m));
        
        // Check for fighter name patterns (single person names, not team names)
        const looksLikeFighter = (name) => {
            if (!name) return false;
            const words = name.trim().split(/\s+/);
            // Fighters typically have 2-4 word names, not city + team name format
            return words.length >= 2 && words.length <= 4 && 
                   !name.match(/\b(city|state|united|fc|club|team|76ers|lakers|warriors|celtics|bulls)\b/i);
        };
        
        // If description looks like a fighter name and title is a common combat market
        if (hasCombatMarket && looksLikeFighter(d)) {
            isCombatSport = true;
        }
    }
    
    return { isSinglePlayer, isCombatSport };
};

const LegBetCard = ({ 
    option, 
    index, 
    isSelected, 
    onCardPress, 
    onMouseEnter, 
    onMouseLeave,
    onSelect,
    onOpenDeepLink,
    onWatchFixture, // Handler for watching "no odds yet" fixtures
    onUnwatchFixture, // Handler for unwatching fixtures
    isWatching = false, // Whether this fixture is currently being watched
    canShowMath,
    showAllMath = true,
    unitSize = 10,
    logoUrl,
    homeLogoUrl,
    awayLogoUrl,
    teamLogoUrl,
    sportsbookLogoUrl,
    kellyCriterion = 0.25, // Default to 1/4 Kelly if not provided
    userSelectedSportsbooks = [], // User's filtered sportsbooks from profile preferences
    marketPeers = [], // ⭐ All options in the same market (for devigged market average)
    defaultSelectedBook = null, // ⭐ For "best X bets" queries - preselects this sportsbook
    highlightPreferredBook = false // ⭐ When true, grey out non-preferred sportsbooks
}) => {
    // Individual glow animation for this card (start at 1 for visibility on web)
    const glowT = useRef(new Animated.Value(1)).current;
    
    // ⭐ RESPONSIVE: Get window dimensions for mobile/tablet detection
    const { width: windowWidth } = useWindowDimensions();
    const isMobileLayout = windowWidth < 768; // Stack columns on mobile/tablet
    const isCompactLayout = windowWidth < 480; // Even more compact on phones
    
    // State for showing math section and books list
    const [showMath, setShowMath] = useState(showAllMath);
    const [showBooks, setShowBooks] = useState(false);
    
    // ⭐ Sync showMath with showAllMath prop when it changes (for Show/Hide All Math button)
    React.useEffect(() => {
        setShowMath(showAllMath);
    }, [showAllMath]);
    const [showBookDetails, setShowBookDetails] = useState(false);
    // ⭐ Initialize selectedBook with defaultSelectedBook if provided (for "best X bets" queries)
    // Check if the default book actually has odds in this option before using it
    const initialBook = React.useMemo(() => {
        if (defaultSelectedBook && option?.bookOdds?.[defaultSelectedBook]) {
            return defaultSelectedBook;
        }
        return 'best';
    }, [defaultSelectedBook, option?.bookOdds]);
    const [selectedBook, setSelectedBook] = useState(initialBook);
    const [sortBooksBy, setSortBooksBy] = useState('ev'); // 'ev', 'odds', or 'mine' - controls sportsbook sorting within this leg
    
    // ⭐ Filter sportsbooks based on user preferences AND handle Blue Book → FanDuel mapping
    // Blue Book is the same as FanDuel, so:
    // - If FanDuel exists, filter out Blue Book
    // - If FanDuel doesn't exist but Blue Book does, rename Blue Book to FanDuel
    const filteredOption = React.useMemo(() => {
        let processedBookOdds = option?.bookOdds ? { ...option.bookOdds } : null;
        let processedAvailableBooks = option?.availableBooks ? [...option.availableBooks] : [];
        
        // 🔄 Handle Blue Book → FanDuel mapping
        if (processedBookOdds) {
            const blueBookKey = Object.keys(processedBookOdds).find(k => 
                k.toLowerCase().includes('blue') && k.toLowerCase().includes('book')
            );
            const fanDuelKey = Object.keys(processedBookOdds).find(k => 
                k.toLowerCase().includes('fanduel')
            );
            
            if (blueBookKey) {
                if (fanDuelKey) {
                    // FanDuel exists, just remove Blue Book
                    delete processedBookOdds[blueBookKey];
                } else {
                    // FanDuel doesn't exist, rename Blue Book to FanDuel
                    processedBookOdds['FanDuel'] = processedBookOdds[blueBookKey];
                    delete processedBookOdds[blueBookKey];
                }
            }
        }
        
        // Also update availableBooks array
        if (processedAvailableBooks.length > 0) {
            const hasFanDuel = processedAvailableBooks.some(b => b.toLowerCase().includes('fanduel'));
            const blueBookIdx = processedAvailableBooks.findIndex(b => 
                b.toLowerCase().includes('blue') && b.toLowerCase().includes('book')
            );
            
            if (blueBookIdx !== -1) {
                if (hasFanDuel) {
                    // Remove Blue Book since FanDuel exists
                    processedAvailableBooks.splice(blueBookIdx, 1);
                } else {
                    // Replace Blue Book with FanDuel
                    processedAvailableBooks[blueBookIdx] = 'FanDuel';
                }
            }
        }
        
        // Now apply user sportsbook filters if any
        if (!userSelectedSportsbooks || userSelectedSportsbooks.length === 0) {
            // No filter applied, return processed option
            return {
                ...option,
                bookOdds: processedBookOdds,
                availableBooks: processedAvailableBooks
            };
        }
        
        // Filter bookOdds to only include selected sportsbooks
        const filteredBookOdds = processedBookOdds ? 
            Object.fromEntries(
                Object.entries(processedBookOdds).filter(([bookName]) => 
                    userSelectedSportsbooks.includes(bookName)
                )
            ) : null;
        
        // Filter availableBooks array
        const filteredAvailableBooks = processedAvailableBooks.filter(bookName => 
            userSelectedSportsbooks.includes(bookName)
        );
        
        return {
            ...option,
            bookOdds: filteredBookOdds && Object.keys(filteredBookOdds).length > 0 ? filteredBookOdds : processedBookOdds,
            availableBooks: filteredAvailableBooks.length > 0 ? filteredAvailableBooks : processedAvailableBooks
        };
    }, [option, userSelectedSportsbooks]);
    
    // Use filtered option for all calculations
    const workingOption = filteredOption || option;
    
    // ⭐ Helper: Extract price from bookOdds (handles both old number format and new object format)
    const extractPrice = (bookOddsValue) => {
        if (bookOddsValue === null || bookOddsValue === undefined) return null;
        // New format: { price: -114, ev_percent: 2.5, ... }
        if (typeof bookOddsValue === 'object' && bookOddsValue.price !== undefined) {
            return bookOddsValue.price;
        }
        // Old format: just a number
        if (typeof bookOddsValue === 'number') return bookOddsValue;
        // Fallback: try to parse as string
        return parseFloat(String(bookOddsValue).replace(/[^-0-9.]/g, ''));
    };
    
    // Math inputs
    const [stake, setStake] = useState(unitSize);
    
    // ⭐ ALWAYS calculate best available odds (for display in "Best Available" row)
    const bestPrice = (() => {
        if (workingOption?.bookOdds && Object.keys(workingOption.bookOdds).length > 0) {
            const allOdds = Object.values(workingOption.bookOdds)
                .map(extractPrice)
                .filter(o => o !== null && o !== undefined && !isNaN(o));
            if (allOdds.length > 0) {
                // Best odds = highest positive OR least negative (closest to 0)
                const best = allOdds.reduce((best, current) => {
                    // Positive odds: higher is better (+200 better than +150)
                    // Negative odds: closer to 0 is better (-110 better than -150)
                    if (current > 0 && best > 0) return current > best ? current : best;
                    if (current < 0 && best < 0) return current > best ? current : best;
                    if (current > 0) return current; // Positive always beats negative
                    if (best > 0) return best;
                    return current > best ? current : best;
                }, allOdds[0]);
                return best;
            }
        }
        // Fallback to raw.price if bookOdds not available
        const m = workingOption?.raw?.price ?? workingOption?.raw?.american_price ?? null;
        return typeof m === 'number' ? m : parseFloat(String(m || '').replace(/[^-0-9.]/g, ''));
    })();
    
    // ⭐ Calculate best available EV% (for "Best Available" row when sorting by EV)
    const bestEvData = (() => {
        if (workingOption?.bookOdds && Object.keys(workingOption.bookOdds).length > 0) {
            let bestEv = null;
            let bestEvOdds = null;
            let bestEvBook = null;
            
            Object.entries(workingOption.bookOdds).forEach(([bookName, bookData]) => {
                const ev = typeof bookData === 'object' ? bookData.ev_percent : null;
                const odds = extractPrice(bookData);
                if (ev !== null && ev !== undefined && (bestEv === null || ev > bestEv)) {
                    bestEv = ev;
                    bestEvOdds = odds;
                    bestEvBook = bookName;
                }
            });
            
            return { ev: bestEv, odds: bestEvOdds, book: bestEvBook };
        }
        return { ev: null, odds: null, book: null };
    })();
    
    // Price for calculations - uses selected book if chosen, otherwise best available
    const price = (() => {
        // If specific book selected, use that book's odds
        if (selectedBook !== 'best' && workingOption?.bookOdds?.[selectedBook]) {
            return extractPrice(workingOption.bookOdds[selectedBook]);
        }
        // Otherwise use best available
        return bestPrice;
    })();
    const implied = (price === null || isNaN(price)) ? null : betMath.americanToImplied(price);
    const stakeNum = typeof stake === 'number' ? stake : (parseFloat(String(stake || '0')) || 0);
    const payout = (() => {
        if (price === null || isNaN(price)) return null;
        const dec = betMath.americanToDecimal(price);
        return dec != null ? stakeNum * dec : null;
    })();
    const aiProb = (() => {
        let rawAiProb = null;
        if (typeof option?.raw?.ai_prob === 'number') {
            rawAiProb = option.raw.ai_prob;
        } else {
            // If an AI fair price were ever included in raw, convert to probability
            const fairAmerican = option?.raw?.ai_price_american;
            if (typeof fairAmerican === 'number') {
                rawAiProb = betMath.americanToImplied(fairAmerican);
            }
        }
        
        if (rawAiProb === null) return null;
        
        // FIX: Check if ai_prob is for the opposite side of the bet
        // If book odds imply >50% but ai_prob is <50% (or vice versa), flip it
        if (implied !== null) {
            const impliedIsFavorite = implied > 0.5;
            const aiProbIsFavorite = rawAiProb > 0.5;
            
            // If they disagree on which side is favored, ai_prob is likely for the opposite side
            if (impliedIsFavorite !== aiProbIsFavorite) {
                // Additional check: if the difference is large (>30%), flip it
                const diff = Math.abs(implied - rawAiProb);
                if (diff > 0.3) {
                    console.log('[LegBetCard] Flipping ai_prob - opposite side detected', {
                        selection: option?.description,
                        price,
                        implied: implied.toFixed(3),
                        rawAiProb: rawAiProb.toFixed(3),
                        flippedAiProb: (1 - rawAiProb).toFixed(3)
                    });
                    return 1 - rawAiProb;
                }
            }
        }
        
        return rawAiProb;
    })();
    // Calculate all 3 devigging methods on-the-fly (if not cached)
    const devigMethods = (() => {
        // ⭐ PRIORITY 1: If a specific sportsbook is selected, use its EV data from bookOdds
        if (selectedBook !== 'best' && workingOption?.bookOdds?.[selectedBook]) {
            const bookData = workingOption.bookOdds[selectedBook];
            // New format: bookOdds[book] is an object with full EV data
            if (typeof bookData === 'object' && bookData.devig_methods) {
                return {
                    additive: {
                        fairProb: bookData.devig_methods.additive_prob,
                        ev: bookData.ev_method1_additive
                    },
                    multiplicative: {
                        fairProb: bookData.devig_methods.multiplicative_prob,
                        ev: bookData.ev_method2_multiplicative
                    },
                    power: bookData.ev_method3_power !== undefined ? {
                        fairProb: bookData.devig_methods.power_prob,
                        ev: bookData.ev_method3_power
                    } : null,
                    methodUsed: bookData.ev_method_used
                };
            }
        }
        
        // ⭐ PRIORITY 2: If "best" is selected, find the ACTUAL best book and use its EV data
        // This ensures the math shown matches what "Best" represents
        if (selectedBook === 'best' && workingOption?.bookOdds) {
            // Find the book with the best odds (same logic as bestPrice)
            let bestBookName = null;
            let bestOddsValue = null;
            
            Object.entries(workingOption.bookOdds).forEach(([bookName, bookData]) => {
                const odds = extractPrice(bookData);
                if (odds !== null && !isNaN(odds)) {
                    if (bestOddsValue === null) {
                        bestOddsValue = odds;
                        bestBookName = bookName;
                    } else {
                        // Better odds: higher positive or less negative
                        const isBetter = (odds > 0 && bestOddsValue > 0 && odds > bestOddsValue) ||
                                         (odds < 0 && bestOddsValue < 0 && odds > bestOddsValue) ||
                                         (odds > 0 && bestOddsValue < 0);
                        if (isBetter) {
                            bestOddsValue = odds;
                            bestBookName = bookName;
                        }
                    }
                }
            });
            
            // Now get the EV data from the actual best book
            if (bestBookName && workingOption.bookOdds[bestBookName]) {
                const bookData = workingOption.bookOdds[bestBookName];
                if (typeof bookData === 'object' && bookData.devig_methods) {
                    return {
                        additive: {
                            fairProb: bookData.devig_methods.additive_prob,
                            ev: bookData.ev_method1_additive
                        },
                        multiplicative: {
                            fairProb: bookData.devig_methods.multiplicative_prob,
                            ev: bookData.ev_method2_multiplicative
                        },
                        power: bookData.ev_method3_power !== undefined ? {
                            fairProb: bookData.devig_methods.power_prob,
                            ev: bookData.ev_method3_power
                        } : null,
                        methodUsed: bookData.ev_method_used,
                        _bestBookName: bestBookName // For debugging
                    };
                }
            }
        }
        
        // ⭐ PRIORITY 3: Fallback to cached devigged data from raw
        if (workingOption?.raw?.devig_methods && workingOption?.raw?.ev_method1_additive !== undefined) {
            return {
                additive: {
                    fairProb: workingOption.raw.devig_methods.additive_prob,
                    ev: workingOption.raw.ev_method1_additive
                },
                multiplicative: {
                    fairProb: workingOption.raw.devig_methods.multiplicative_prob,
                    ev: workingOption.raw.ev_method2_multiplicative
                },
                power: workingOption.raw.ev_method3_power !== undefined ? {
                    fairProb: workingOption.raw.devig_methods.power_prob,
                    ev: workingOption.raw.ev_method3_power
                } : null,
                methodUsed: workingOption.raw.ev_method_used
            };
        }
        
        // ⭐ PRIORITY 4 (v1.18 P1): on-the-fly devig delegates to the canonical
        // implementation shared with UnifiedBetCard / BetCardV2 / the results
        // sort (utils/canonicalEv.computeDevigMethodsSnapshot). The old inline
        // math had real bugs: its "additive" formula was actually the
        // multiplicative one (implied / totalImplied — no overround split), its
        // power method was a heuristic k-iteration instead of the binary-search
        // solver, and it assumed flat 5% vig instead of the shared conventions
        // (peer-averaged totalImplied → ev_percent-derived vig → 10% flat) — so
        // this card could disagree with every other surface for the same bet.
        if (implied === null || price === null) return null;
        return computeDevigMethodsSnapshot(workingOption, marketPeers || [], price);
    })();
    
    const consensusOptions = useConsensusOptions();
    const ev = (() => {
        // ⭐ v1.18 P1 — LegBetCard's EV now flows through the SAME canonical
        // per-book math as the UnifiedBetCard pill, BetCardV2 and the results
        // sort (utils/canonicalEv.cleanPerBookEv). Previously this read legacy
        // stored ev_percent stamps and a bespoke peers/5%-vig fallback, which
        // could disagree with every other surface for the same bet.
        const raw = workingOption?.raw || {};
        const isSGPItem = raw.ev_method === 'individual_leg_devig';
        // Sanity cap (unchanged): |EV| > 50 almost always means an
        // alternate-line/data mismatch, not a genuine edge — show nothing.
        const _sane = (v) => (Number.isFinite(v) && Math.abs(v) <= 50) ? v : null;

        // PRIORITY 1: specific book selected → canonical per-book EV.
        if (selectedBook !== 'best' && workingOption?.bookOdds?.[selectedBook]) {
            const bookData = workingOption.bookOdds[selectedBook];
            const cpe = cleanPerBookEv(raw, bookData, isSGPItem, consensusOptions, selectedBook);
            if (Number.isFinite(cpe)) return _sane(cpe);
            if (typeof bookData === 'object' && Number.isFinite(bookData.ev_percent)) {
                return _sane(bookData.ev_percent);
            }
        }

        // PRIORITY 2: "best" → max canonical EV across all books (same axis the
        // results sort and the UnifiedBetCard auto-select use).
        const bo = workingOption?.bookOdds || raw.bookOdds || {};
        let best = null;
        for (const [bk, bd] of Object.entries(bo)) {
            const wc = cleanPerBookEv(raw, bd, isSGPItem, consensusOptions, bk);
            if (Number.isFinite(wc) && (best == null || wc > best)) best = wc;
        }
        if (best != null) return _sane(best);

        // PRIORITY 3: backend headline EV — only trusted when devig_methods
        // exists (never the legacy ai_prob-derived stamps).
        if (raw.ev_percent != null && raw.devig_methods) return _sane(raw.ev_percent);

        // PRIORITY 4: min of the devig method EVs (canonical snapshot above).
        if (devigMethods && devigMethods.additive?.ev != null) {
            const vals = [devigMethods.additive.ev, devigMethods.multiplicative?.ev, devigMethods.power?.ev]
                .filter(v => v != null && Number.isFinite(v));
            if (vals.length > 0) return _sane(Math.min(...vals));
        }
        return null;
    })();

    // ⭐ TRUE DEVIGGED MARKET AVERAGE - Averages implied probabilities across all books, then devigs
    const marketAverage = (() => {
        const toImpliedProb = (american) => betMath.americanToImplied(american);

        // Helper to get ALL prices from bookOdds as array
        const getAllPrices = (bookOdds) => {
            if (!bookOdds || typeof bookOdds !== 'object') return [];
            return Object.values(bookOdds)
                .map(o => {
                    if (typeof o === 'object' && o !== null && o.price !== undefined) return o.price;
                    if (typeof o === 'number') return o;
                    return null;
                })
                .filter(p => p !== null && !isNaN(p));
        };
        
        // Get all prices for current selection
        const currentPrices = getAllPrices(workingOption?.bookOdds);
        if (currentPrices.length === 0) return null;
        
        // ⭐ Find opposite selection from marketPeers
        const currentSelection = (workingOption?.selection || workingOption?.raw?.selection || '').toLowerCase();
        const oppositeOption = marketPeers.find(peer => {
            const peerSelection = (peer?.selection || peer?.raw?.selection || '').toLowerCase();
            return peerSelection !== currentSelection && peerSelection.length > 0;
        });
        
        // If we found opposite side, calculate TRUE devigged market average
        if (oppositeOption) {
            const oppPrices = getAllPrices(oppositeOption?.bookOdds);
            if (oppPrices.length > 0) {
                // ⭐ Step 1: Convert all odds to implied probabilities
                const currentImpliedProbs = currentPrices.map(toImpliedProb);
                const oppImpliedProbs = oppPrices.map(toImpliedProb);
                
                // ⭐ Step 2: Average the implied probabilities for each side
                const avgCurrentImplied = currentImpliedProbs.reduce((sum, p) => sum + p, 0) / currentImpliedProbs.length;
                const avgOppImplied = oppImpliedProbs.reduce((sum, p) => sum + p, 0) / oppImpliedProbs.length;
                
                // ⭐ Step 3: Calculate total (includes vig)
                const totalImplied = avgCurrentImplied + avgOppImplied;
                
                // ⭐ Step 4: Remove vig - fair probability = avgImplied / totalImplied
                const fairProb = avgCurrentImplied / totalImplied;
                
                // ⭐ Step 5: Convert fair probability to American odds
                if (fairProb > 0.5) {
                    return Math.round(-100 * fairProb / (1 - fairProb));
                } else {
                    return Math.round(100 * (1 - fairProb) / fairProb);
                }
            }
        }
        
        // Fallback: average implied probabilities for just this side (not devigged, but better than nothing)
        if (currentPrices.length > 0) {
            const impliedProbs = currentPrices.map(toImpliedProb);
                const avgProb = impliedProbs.reduce((sum, prob) => sum + prob, 0) / impliedProbs.length;
                
                if (avgProb > 0.5) {
                    return Math.round(-100 * avgProb / (1 - avgProb));
                } else {
                    return Math.round(100 * (1 - avgProb) / avgProb);
            }
        }
        return null;
    })();

    const marketAverageImplied = marketAverage === null ? null : betMath.americanToImplied(marketAverage);

    const fairPrice = aiProb === null ? null : betMath.impliedToAmerican(aiProb);

    const fairPriceImplied = fairPrice === null ? null : betMath.americanToImplied(fairPrice);

    // Kelly Criterion — ⭐ v1.18 P1: Kelly shares the card's EV-derived fair
    // prob (same inversion as UnifiedBetCard): p = (ev/100 + 1) / decimal at
    // the displayed price. Guarantees a +EV card never shows Kelly 0% and a
    // −EV card always does. aiProb remains only as a legacy fallback for rows
    // where no EV could be computed at all.
    const kellyUnits = (() => {
        let p = null;
        if (ev != null && Number.isFinite(ev) && price != null && !isNaN(price)) {
            const dec = betMath.americanToDecimal(price);
            const cand = dec != null ? (ev / 100 + 1) / dec : null;
            if (cand > 0 && cand < 1) p = cand;
        }
        if (p === null) p = aiProb;
        if (p === null || implied === null) return null;
        const kelly = betMath.kellyFraction(p, price, kellyCriterion);
        if (kelly == null) return null;
        // Cap at 50% of bankroll for safety (even full Kelly shouldn't exceed this)
        return Math.max(0, Math.min(kelly, 0.5));
    })();

    const suggestedWager = kellyUnits !== null && kellyUnits > 0 ? Math.max(1, Math.round(stakeNum * kellyUnits)) : null;

    // Book count and individual book data from bookOdds - SORTED BY BEST ODDS
    const bookOddsData = (() => {
        let data = [];
        
        // Priority 1: Use bookOdds object if available
        if (workingOption?.bookOdds && typeof workingOption.bookOdds === 'object') {
            data = Object.entries(workingOption.bookOdds)
                .filter(([book, odds]) => odds !== null && odds !== undefined)
                .map(([book, odds]) => ({ book, odds }));
        }
        // Priority 2: Use raw.bookOdds if available
        else if (workingOption?.raw?.bookOdds && typeof workingOption.raw.bookOdds === 'object') {
            data = Object.entries(workingOption.raw.bookOdds)
                .filter(([book, odds]) => odds !== null && odds !== undefined)
                .map(([book, odds]) => ({ book, odds }));
        }
        // Priority 3: Fallback to availableBooks (legacy)
        else if (workingOption?.availableBooks && Array.isArray(workingOption.availableBooks)) {
            return workingOption.availableBooks.map(book => ({ book, odds: null }));
        }
        
        // ⭐ CRITICAL FIX: Sort by best odds so "best available" appears first
        // This ensures the displayed books match the "best price" calculation
        data.sort((a, b) => {
            const priceA = extractPrice(a.odds);
            const priceB = extractPrice(b.odds);
            if (priceA === null && priceB === null) return 0;
            if (priceA === null) return 1;
            if (priceB === null) return -1;
            // Higher odds are better (positive: +200 > +150, negative: -110 > -150)
            return priceB - priceA;
        });
        
        return data;
    })();
    
    const bookCount = bookOddsData.length;
    const [showAllBooks, setShowAllBooks] = useState(false);
    const displayedBooks = showAllBooks ? bookOddsData : bookOddsData.slice(0, 5);

    const handleMouseEnter = () => {
        Animated.timing(glowT, { 
            toValue: 1, 
            duration: 1500, 
            easing: Easing.bezier(0.22, 1, 0.36, 1), 
            useNativeDriver: true 
        }).start();
        
        if (onMouseEnter) onMouseEnter();
    };

    const handleMouseLeave = () => {
        if (!isSelected) {
            Animated.timing(glowT, { 
                toValue: 0, 
                duration: 1500, 
                easing: Easing.bezier(0.22, 1, 0.36, 1), 
                useNativeDriver: true 
            }).start();
        }
        
        if (onMouseLeave) onMouseLeave();
    };

    const handleCardPress = () => {
        if (onSelect) {
            onSelect(option);
        } else if (onCardPress) {
            onCardPress(index);
        }
    };

    const handleSelectLeg = () => {
        if (onSelect) {
            onSelect(option);
        } else if (onCardPress) {
            onCardPress(index);
        }
    };

    const handleToggleMath = () => {
        setShowMath(!showMath);
    };
    
    // Check if this is a "no odds yet" fixture card
    const isNoOddsYet = option?._noOddsYet === true;
    const isLive = option?._isLive === true;
    const isEnded = option?._isEnded === true;

    // ⭐ Helper: Get the DISPLAY EV for the three methods (fall back to main EV if methods are null or zero)
    const displayEvMethods = React.useMemo(() => {
        // If devigMethods has valid NON-ZERO data, use it
        // Check for actual non-zero EV values (0.00% is likely a failed calculation)
        const hasValidDevigMethods = devigMethods && 
            devigMethods.additive?.ev !== undefined && 
            (Math.abs(devigMethods.additive.ev) > 0.001 || Math.abs(devigMethods.multiplicative?.ev || 0) > 0.001);
        
        if (hasValidDevigMethods) {
            return devigMethods;
        }
        
        // If we have a real ev_percent from backend (non-zero), use it for all three as a fallback
        // This handles cases where the on-the-fly calculation fails (non-2-way markets like player props)
        if (ev !== null && ev !== undefined && Math.abs(ev) > 0.001) {
            return {
                additive: { fairProb: null, ev: ev },
                multiplicative: { fairProb: null, ev: ev },
                power: { fairProb: null, ev: ev },
                methodUsed: workingOption?.raw?.ev_method_used || 'power'
            };
        }
        
        // If devigMethods exists but has zero values, still return it (actual zero EV)
        if (devigMethods && devigMethods.additive?.ev !== undefined) {
            return devigMethods;
        }
        
        return null;
    }, [devigMethods, ev, workingOption?.raw?.ev_method_used]);

  return (
    <LegBetCardView
      awayLogoUrl={awayLogoUrl}
      bestEvData={bestEvData}
      bestPrice={bestPrice}
      bookCount={bookCount}
      canShowMath={canShowMath}
      defaultSelectedBook={defaultSelectedBook}
      detectSportType={detectSportType}
      displayEvMethods={displayEvMethods}
      ev={ev}
      extractPrice={extractPrice}
      handleCardPress={handleCardPress}
      handleMouseEnter={handleMouseEnter}
      handleMouseLeave={handleMouseLeave}
      handleSelectLeg={handleSelectLeg}
      handleToggleMath={handleToggleMath}
      highlightPreferredBook={highlightPreferredBook}
      homeLogoUrl={homeLogoUrl}
      implied={implied}
      isCompactLayout={isCompactLayout}
      isEnded={isEnded}
      isLive={isLive}
      isMobileLayout={isMobileLayout}
      isNoOddsYet={isNoOddsYet}
      isSelected={isSelected}
      isWatching={isWatching}
      kellyUnits={kellyUnits}
      logoUrl={logoUrl}
      marketAverage={marketAverage}
      marketAverageImplied={marketAverageImplied}
      onOpenDeepLink={onOpenDeepLink}
      onUnwatchFixture={onUnwatchFixture}
      onWatchFixture={onWatchFixture}
      option={option}
      payout={payout}
      price={price}
      selectedBook={selectedBook}
      setSelectedBook={setSelectedBook}
      setSortBooksBy={setSortBooksBy}
      setStake={setStake}
      showMath={showMath}
      sortBooksBy={sortBooksBy}
      sportsbookLogoUrl={sportsbookLogoUrl}
      stakeNum={stakeNum}
      suggestedWager={suggestedWager}
      teamLogoUrl={teamLogoUrl}
      userSelectedSportsbooks={userSelectedSportsbooks}
      workingOption={workingOption}
    />
  );
};


export default LegBetCard;
